import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export type ExportMetric = 'requests' | 'volume' | 'bandwidth';
export type ExportView = 'domain' | 'day' | 'domain_day' | 'geo';

export interface UsageExportParams {
  /** Service-scoped export when set; org-wide otherwise. */
  serviceId?: string;
  from: string; // RFC3339
  to: string; // RFC3339
  metrics: ExportMetric[];
  views: ExportView[];
}

/** Read the server-provided filename from Content-Disposition, else fall back. */
function filenameFrom(disposition: unknown, fallback: string): string {
  const m = typeof disposition === 'string' ? disposition.match(/filename="?([^"]+)"?/i) : null;
  return m?.[1] || fallback;
}

/**
 * Download the usage report as .xlsx. Goes through the axios client (carries the
 * auth header) as a blob, then triggers a browser download — a plain <a href>
 * wouldn't send credentials. The backend streams a multi-sheet workbook.
 */
export async function exportUsageWorkbook(p: UsageExportParams): Promise<void> {
  const url = p.serviceId
    ? `/services/${p.serviceId}/cdn/business/export`
    : `/cdn/analytics/usage/export`;
  let res;
  try {
    res = await serviceApiV3.get(url, {
      responseType: 'blob',
      params: { from: p.from, to: p.to, metrics: p.metrics.join(','), views: p.views.join(',') },
    });
  } catch (e) {
    // With responseType:'blob' axios delivers error bodies as Blobs too — decode
    // back to JSON so callers can show the server's message, not a generic one.
    const errRes = (e as { response?: { data?: unknown } })?.response;
    if (errRes?.data instanceof Blob) {
      try {
        errRes.data = JSON.parse(await errRes.data.text());
      } catch {
        /* body wasn't JSON — keep the Blob */
      }
    }
    throw e;
  }
  const blob = new Blob([res.data as BlobPart], { type: XLSX_MIME });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filenameFrom(res.headers?.['content-disposition'], 'ContentDeliveryNetwork_BusinessUsages.xlsx');
  a.click();
  URL.revokeObjectURL(href);
}

export function useUsageExport(options?: UseMutationOptions<void, unknown, UsageExportParams>) {
  return useMutation({ mutationFn: exportUsageWorkbook, ...options });
}
